import { createSignal, For, onCleanup, onMount, Show } from 'solid-js';
import { A } from '@solidjs/router';
import { t } from '@/shared/i18n';
import { usePublicPath } from '@/shared/lib/usePublicPath';
import { trackMarketingEvent } from '@/shared/lib/marketing-analytics';
import { IsometricBg } from '@/shared/components/IsometricBg';
import MarketingNavbar from './components/MarketingNavbar';

// Slate-700 boxes on a white background
const BOX_COLOR: [number, number, number] = [51, 65, 85];

/** Static plan metadata — text comes from i18n keys */
const planDefs = [
  {
    key: 'free',
    monthlyPrice: 0,
    annualPrice: 0,
    highlighted: false,
    ctaLink: '/signup',
    featureCount: 5,
  },
  {
    key: 'pro',
    monthlyPrice: 14.99,
    annualPrice: 12.49,
    highlighted: true,
    ctaLink: '/signup?plan=pro',
    featureCount: 7,
  },
  {
    key: 'enterprise',
    monthlyPrice: 78.99,
    annualPrice: 65.83,
    highlighted: false,
    ctaLink: 'mailto:human.khoai@gmail.com',
    featureCount: 7,
  },
] as const;

const addonKeys = ['storehouse', 'users', 'items', 'transactions'] as const;
const faqKeys = [
  'trial',
  'change',
  'payment',
  'cancel',
  'refund',
  'vnd',
  'data',
] as const;

export default function PricingPage() {
  const [annual, setAnnual] = createSignal(false);
  const pp = usePublicPath();

  onMount(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'khoai.io',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web, iOS, Android',
      url: 'https://khoai.io/pricing',
      offers: [
        {
          '@type': 'Offer',
          name: 'Free',
          price: '0',
          priceCurrency: 'USD',
          description:
            'Free forever plan for small warehouse and store operations.',
          url: 'https://khoai.io/pricing',
        },
        {
          '@type': 'Offer',
          name: 'Pro',
          price: '14.99',
          priceCurrency: 'USD',
          description:
            'Pro plan for growing operations with AI analytics and advanced features.',
          url: 'https://khoai.io/pricing',
        },
        {
          '@type': 'Offer',
          name: 'Enterprise',
          price: '78.99',
          priceCurrency: 'USD',
          description:
            'Enterprise plan for large multi-warehouse operations with full feature access.',
          url: 'https://khoai.io/pricing',
        },
      ],
    };
    let script = document.getElementById(
      'ld-json-pricing'
    ) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'ld-json-pricing';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
    onCleanup(() => document.getElementById('ld-json-pricing')?.remove());
  });

  return (
    <div class="relative flex min-h-screen flex-col bg-white text-gray-900">
      <IsometricBg
        class="fixed inset-0 z-0 h-full w-full"
        count={30}
        color={BOX_COLOR}
      />

      <MarketingNavbar lightTheme />

      {/* Header */}
      <section class="relative z-10 py-16 sm:py-20">
        <div class="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <h1 class="text-4xl font-extrabold text-gray-900 sm:text-5xl">
            {t('pricing.title')}
          </h1>
          <p class="mx-auto mt-4 max-w-xl text-lg text-gray-500">
            {t('pricing.subtitle')}
          </p>

          {/* Trust signals */}
          <div class="mt-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <span class="flex items-center gap-1.5 text-sm text-gray-500">
              <span class="font-semibold text-green-500">&#10003;</span>
              {t('pricing.trust.freePlan')}
            </span>
            <span class="flex items-center gap-1.5 text-sm text-gray-500">
              <span class="font-semibold text-green-500">&#10003;</span>
              {t('pricing.trust.noCard')}
            </span>
            <span class="flex items-center gap-1.5 text-sm text-gray-500">
              <span class="font-semibold text-green-500">&#10003;</span>
              {t('pricing.trust.cancel')}
            </span>
          </div>

          {/* Billing toggle */}
          <div class="mt-8 flex items-center justify-center gap-4">
            <span
              class={`text-base font-semibold ${!annual() ? 'text-gray-900' : 'text-gray-400'}`}
            >
              {t('pricing.monthly')}
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={annual()}
              onClick={() => setAnnual(!annual())}
              class={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                annual() ? 'bg-amber-600' : 'bg-gray-300'
              }`}
            >
              <span
                class={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  annual() ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
            <span
              class={`text-base font-semibold ${annual() ? 'text-gray-900' : 'text-gray-400'}`}
            >
              {t('pricing.annual')}
              <span class="ml-2 rounded-full bg-green-100 px-2.5 py-1 text-sm font-semibold text-green-700">
                {t('pricing.save')}
              </span>
            </span>
          </div>
        </div>
      </section>

      {/* Plan cards */}
      <section class="relative z-10 pb-20">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div class="grid gap-8 lg:grid-cols-3">
            <For each={planDefs}>
              {(plan) => {
                const price = () =>
                  annual() ? plan.annualPrice : plan.monthlyPrice;
                const isEnterprise = plan.key === 'enterprise';
                const features = () =>
                  Array.from({ length: plan.featureCount }, (_, i) =>
                    t(`pricing.${plan.key}.f${i + 1}`)
                  );

                return (
                  <div
                    class={`relative flex flex-col rounded-2xl border p-8 ${
                      plan.highlighted
                        ? 'border-amber-500 shadow-2xl ring-2 ring-amber-500/30'
                        : 'border-gray-200 shadow-sm'
                    } bg-white/90 backdrop-blur-sm`}
                  >
                    <Show when={plan.highlighted}>
                      <span class="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-amber-600 px-3 py-1 text-xs font-semibold text-white shadow-sm">
                        {t('pricing.recommended')}
                      </span>
                    </Show>

                    <h3 class="text-lg font-semibold text-gray-900">
                      {t(`pricing.${plan.key}.name`)}
                    </h3>
                    <p class="mt-1 text-sm text-gray-500">
                      {t(`pricing.${plan.key}.desc`)}
                    </p>

                    <div class="mt-6">
                      <span class="text-4xl font-extrabold text-gray-900">
                        ${price().toFixed(2)}
                      </span>
                      <span class="text-sm text-gray-500">
                        {t('pricing.perMonth')}
                      </span>
                      <Show when={annual() && plan.monthlyPrice > 0}>
                        <p class="mt-1 text-xs text-gray-500">
                          {t('pricing.billedAnnually', {
                            amount: `$${(plan.annualPrice * 12).toFixed(2)}`,
                          })}
                        </p>
                      </Show>
                    </div>

                    <ul class="mt-8 flex-1 space-y-3">
                      <For each={features()}>
                        {(feature) => (
                          <li class="flex items-start gap-2 text-sm text-gray-600">
                            <span class="mt-0.5 text-green-500">✓</span>
                            {feature}
                          </li>
                        )}
                      </For>
                    </ul>

                    <Show
                      when={!isEnterprise}
                      fallback={
                        <a
                          href={plan.ctaLink}
                          onClick={() =>
                            trackMarketingEvent('pricing_cta_click', {
                              plan: plan.key,
                              billingCycle: annual() ? 'annual' : 'monthly',
                              target: plan.ctaLink,
                              type: 'contact_sales',
                            })
                          }
                          class="mt-8 block rounded-lg border border-gray-300 px-4 py-3 text-center text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                        >
                          {t(`pricing.${plan.key}.cta`)}
                        </a>
                      }
                    >
                      <A
                        href={plan.ctaLink}
                        onClick={() =>
                          trackMarketingEvent('pricing_cta_click', {
                            plan: plan.key,
                            billingCycle: annual() ? 'annual' : 'monthly',
                            target: plan.ctaLink,
                            type: 'signup',
                          })
                        }
                        class={`mt-8 block rounded-lg px-4 py-3 text-center text-sm font-semibold transition-colors ${
                          plan.highlighted
                            ? 'bg-amber-600 text-white hover:bg-amber-700'
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {t(`pricing.${plan.key}.cta`)}
                      </A>
                      <Show when={plan.key === 'pro'}>
                        <p class="mt-2 text-center text-xs text-gray-400">
                          {t('pricing.trust.noCard')}
                        </p>
                      </Show>
                    </Show>
                  </div>
                );
              }}
            </For>
          </div>

          <p class="mt-6 text-center text-sm text-gray-500">
            {t('pricing.vndNote')}
          </p>
        </div>
      </section>

      {/* Add-ons */}
      <section class="relative z-10 py-16 sm:py-20">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div class="text-center">
            <h2 class="text-2xl font-bold text-gray-900 sm:text-3xl">
              {t('pricing.addons.title')}
            </h2>
            <p class="mt-2 text-gray-500">{t('pricing.addons.subtitle')}</p>
          </div>
          <div class="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <For each={addonKeys}>
              {(key) => (
                <div class="rounded-xl border border-gray-200 bg-white/80 p-6 text-center backdrop-blur-sm">
                  <h3 class="font-semibold text-gray-900">
                    {t(`pricing.addon.${key}.name`)}
                  </h3>
                  <p class="mt-2 text-sm text-gray-500">
                    {t(`pricing.addon.${key}.price`)}
                  </p>
                </div>
              )}
            </For>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section class="relative z-10 py-16 sm:py-20">
        <div class="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 class="text-center text-2xl font-bold text-gray-900 sm:text-3xl">
            {t('pricing.faq.title')}
          </h2>
          <dl class="mt-12 space-y-8">
            <For each={faqKeys}>
              {(key) => (
                <div>
                  <dt class="text-base font-semibold text-gray-900">
                    {t(`pricing.faq.${key}.q`)}
                  </dt>
                  <dd class="mt-2 text-sm leading-relaxed text-gray-500">
                    {t(`pricing.faq.${key}.a`)}
                  </dd>
                </div>
              )}
            </For>
          </dl>
        </div>
      </section>

      {/* Footer */}
      <footer class="relative z-10 border-t border-gray-200 bg-white/80 py-8 backdrop-blur-sm">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div class="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <p class="text-sm text-gray-400">{t('public.footer.copyright')}</p>
            <div class="flex gap-6">
              <A
                href={pp('/privacy')}
                class="text-sm text-gray-400 hover:text-gray-700"
              >
                {t('public.footer.privacy')}
              </A>
              <A
                href={pp('/terms')}
                class="text-sm text-gray-400 hover:text-gray-700"
              >
                {t('public.footer.terms')}
              </A>
              <A
                href={pp('/billing-terms')}
                class="text-sm text-gray-400 hover:text-gray-700"
              >
                {t('public.footer.billingTerms')}
              </A>
              <a
                href="mailto:human.khoai@gmail.com"
                class="text-sm text-gray-400 hover:text-gray-700"
              >
                {t('public.footer.contact')}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
